import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  IconButton,
  ListItemText,
  Paper,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { showTextInLanguage } from '../../helpers';
import { useGetHotelData, useMappedAmenitiesByDefaultProvider, useUpdateHotelAmenitiesMutation } from '../../queries';
import { Close } from '@mui/icons-material';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { HotelAmentityDTO } from '@bandido/shared/dtos';
import { useLoader } from './Loader';
import { useQueryClient } from 'react-query';
import { QueryKeys } from '@frontend/features/common/types';
import { AmenityItem } from './AmenityItem';

const ID_KEY = 'amenity-editor';

const AmenityListWraper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'start',
  flexWrap: 'wrap',
  padding: theme.spacing(4),
}));

const AmenityListItemWrapper = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  width: '380px',
  padding: theme.spacing(2),
}));

export const AmenityEditor: FC = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { hotelId } = useParams();
  const [ selectedAmenitites, setSelectedAmenities] = useState<HotelAmentityDTO[]>([]);
  const { t, i18n } = useTranslation();
  const { addToast } = useSnackBar();
  const { toggleLoader } = useLoader();

  const {
    isLoading: loadingData,
    data: hotel
  } = useGetHotelData({
    hotelId: `${hotelId}`,
  });

  const {
    data: mappedAmenities,
    isLoading: mappedAmenitiesLoading
  } = useMappedAmenitiesByDefaultProvider();

  const { mutate: updateAmenities, isLoading: isUpdating} = useUpdateHotelAmenitiesMutation();

  const handleUpdateAmenities = async () => {
    if (hotelId) {
      toggleLoader(ID_KEY, true);
      updateAmenities({
        hotelId,
        ids: selectedAmenitites.map(({id}) => id),
      }, {
        onSuccess: () => {
          queryClient.invalidateQueries([QueryKeys.getHotelData, hotelId]);
          queryClient.invalidateQueries([QueryKeys.getHotelCardData, hotelId]);
          addToast({
            color: 'success',
            message: t('hotels.contentManager.detail.amenities.update.success')
          });
        },
        onError: () => {
          addToast({
            color: 'error',
            message: t('hotels.contentManager.detail.amenities.update.error')
          });
        },
        onSettled: () => {
          toggleLoader(ID_KEY, false);
        }
      });
    }
  };

  const handleRemoveAmenities = (amenityId: number) => {
    setSelectedAmenities((prev) => prev.filter(({id}) => id !== amenityId) );
  };

  const handleSelectAmenitites = (_e: SyntheticEvent, value: HotelAmentityDTO[]) => {
    setSelectedAmenities(value);
  };

  useEffect(() => {
    if (hotel?.amenities?.length) {
      setSelectedAmenities(hotel.amenities);
    }
  }, [hotel]);

  useEffect(() => {
    toggleLoader(ID_KEY, mappedAmenitiesLoading);
  }, [mappedAmenitiesLoading]);

  return (
    <Box padding={theme.spacing(2)}>
      <Card elevation={2} sx={{ mt: theme.spacing(2) }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            pb={theme.spacing(2)}
          >
            <Typography variant="h6">
              {t('hotels.contentManager.detail.amenities.title')}
            </Typography>
            <Box display='flex' gap={2}>
              <Autocomplete
                multiple
                title={!mappedAmenities?.length ? t('hotels.contentManager.configuration.tabs.amenitiesMapping.notMapped') : undefined}
                disabled={!mappedAmenities?.length}
                disableCloseOnSelect
                size="small"
                loading={mappedAmenitiesLoading}
                options={mappedAmenities || []}
                value={selectedAmenitites}
                filterOptions={
                  (options, { inputValue }) => options.filter(
                    (option) => option.systemMappingKey?.toLocaleLowerCase().includes(inputValue)
                  )
                } 
                onChange={handleSelectAmenitites}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={({texts, code}) => texts.length ? showTextInLanguage(texts, i18n.language) : code}
                renderOption={(props, amenity, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    <ListItemText
                      primary={
                        <AmenityItem amenity={amenity}/>
                      }
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {mappedAmenitiesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        </>
                      ),
                    }}
                    variant="outlined"
                  />
                )}
                renderTags={() => null}
                sx={{ width: 300}}
              />
              <Button
                variant="contained"
                color="primary"
                size='small'
                disabled={isUpdating || loadingData}
                onClick={handleUpdateAmenities}
              >
                {t('common.form.save')}
              </Button>
            </Box>
          </Box>
          <Box minHeight={200}>
            <AmenityListWraper gap={2}>
              {selectedAmenitites?.map((amenity) => (
                <Paper elevation={1} key={amenity.id}>
                  <AmenityListItemWrapper gap={2}>
                    <AmenityItem amenity={amenity} showMappingStatus/>
                    <IconButton
                      color='error'
                      onClick={() => handleRemoveAmenities(amenity.id)}
                    >
                      <Close/>
                    </IconButton>
                  </AmenityListItemWrapper>
                </Paper>
              ))}
            </AmenityListWraper>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
